// ============================================================================
// EMPLOYEE TERMINATE MODAL
// ============================================================================
// Drives the destructive Termination flow for prima.tbl_employee. Pattern
// mirrors the Engagement Close confirm flow:
//
//   1. User opens the modal from the Employee Master row action menu.
//   2. Modal lazy-fetches the termination preview for today's date.
//   3. User adjusts last-working-day or types the reason — the date change
//      re-fetches the preview (debounced) so blockers and the auto-submit
//      list stay in sync with the chosen date.
//   4. Confirm is disabled when the preview reports `canTerminate=false`
//      or when the reason is < 5 chars.
//   5. On confirm, POST /Employees/{id}/terminate with the rowVersion
//      returned by preview. Server re-runs all blockers — preview is
//      advisory.
//
// Terminating is one-way in v1 — there is no reactivation. v2 will add it.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { useTerminationPreview, useTerminateEmployee } from 'hooks/api/useEmployee';
import type { Employee, UnsubmittedWeek } from 'api/types';

interface EmployeeTerminateModalProps {
  isOpen: boolean;
  employee: Employee | null;
  onClose: () => void;
  onTerminated: (terminated: Employee) => void;
}

const todayIso = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatDate = (iso?: string | null): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatMinutes = (m: number): string => {
  if (!m || m <= 0) return '0h';
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r === 0 ? `${h}h` : `${h}h ${r}m`;
};

const plannedActionLabel = (action: UnsubmittedWeek['plannedAction']): { label: string; cls: string } => {
  switch (action) {
    case 'AUTO_SUBMIT_ZERO': return { label: 'Will auto-submit (0h)', cls: 'badge badge-pending' };
    case 'BLOCK':            return { label: 'Blocking',              cls: 'badge badge-inactive' };
    default:                 return { label: 'OK',                    cls: 'badge badge-active' };
  }
};

const EmployeeTerminateModal: React.FC<EmployeeTerminateModalProps> = ({
  isOpen, employee, onClose, onTerminated,
}) => {
  const [dateLeft, setDateLeft] = useState<string>(todayIso());
  const [reason, setReason] = useState<string>('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reasonTouched, setReasonTouched] = useState(false);

  const { data: preview, loading: previewLoading, error: previewError, fetchPreview, reset: resetPreview } =
    useTerminationPreview();
  const { terminate, loading: terminateLoading } = useTerminateEmployee();

  // ---- Lifecycle ----
  // Reset form on each open. Last-working-day defaults to today.
  useEffect(() => {
    if (!isOpen) return;
    setDateLeft(todayIso());
    setReason('');
    setReasonTouched(false);
    resetPreview();
  }, [isOpen, resetPreview]);

  // ---- Debounced preview fetch ----
  // We fire on every dateLeft change (after 350ms) so the user sees the
  // weeks-to-be-auto-submitted list update live as they pick a date.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!isOpen || !employee) return;
    if (!dateLeft) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchPreview(employee.id, dateLeft).catch(() => { /* swallow — error surfaces via previewError */ });
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [isOpen, employee, dateLeft, fetchPreview]);

  // ---- Validation ----
  const reasonError = useMemo(() => {
    if (!reasonTouched) return null;
    const trimmed = reason.trim();
    if (trimmed.length === 0) return 'Reason is required.';
    if (trimmed.length < 5)  return 'Reason must be at least 5 characters.';
    return null;
  }, [reason, reasonTouched]);

  const canConfirm =
    !!preview &&
    preview.canTerminate &&
    !previewLoading &&
    !terminateLoading &&
    reason.trim().length >= 5;

  // ---- Actions ----
  const handleConfirmClick = useCallback(() => {
    setReasonTouched(true);
    if (!canConfirm) return;
    setConfirmOpen(true);
  }, [canConfirm]);

  const handleDoTerminate = useCallback(async () => {
    if (!employee || !preview) return;
    try {
      const result = await terminate(employee.id, {
        dateLeft,
        reason: reason.trim(),
        rowVersion: preview.rowVersion,
      });
      setConfirmOpen(false);
      notificationService.toast.success(`${employee.code} — ${employee.name} terminated.`);
      onTerminated(result);
    } catch (err: any) {
      setConfirmOpen(false);
      const apiBody = err?.response?.data;
      const message =
        (typeof apiBody?.message === 'string' && apiBody.message) ||
        err?.message ||
        'Failed to terminate employee.';
      notificationService.toast.error(message, 6000);
    }
  }, [employee, preview, dateLeft, reason, terminate, onTerminated]);

  if (!isOpen || !employee) return null;

  const blockers = preview?.blockers ?? [];
  const weeks = preview?.unsubmittedWeeks ?? [];
  const policy = preview?.unsubmittedPolicy ?? 'BLOCK';

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">

        {(previewLoading || terminateLoading) && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#dc2626', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              {terminateLoading ? 'Terminating…' : 'Loading preview…'}
            </span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <IconSystem.alert size={18} />
            Terminate Employee — {employee.code} · {employee.name}
          </h2>
          <button className="modal-close" onClick={onClose} title="Close" disabled={terminateLoading}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ fontSize: '0.85rem' }}>

          {/* Form row */}
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">
                Last Working Day <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className="form-input"
                value={dateLeft}
                max={todayIso()}
                onChange={(e) => setDateLeft(e.target.value)}
                disabled={terminateLoading}
              />
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                Date the employee actually stopped working.
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Resolved Policy</label>
              <div style={{
                padding: '0.4rem 0.5rem',
                background: 'var(--bg-secondary)',
                borderRadius: '0.375rem',
                border: '1px solid var(--border-color)',
                fontWeight: 600,
              }}>
                {policy === 'AUTO_SUBMIT_ZERO' ? 'AUTO_SUBMIT_ZERO (auto-submit not-started weeks)' : 'BLOCK (default)'}
              </div>
            </div>
          </div>

          <div className="form-group" style={{ marginTop: '0.75rem' }}>
            <label className="form-label">
              Reason <span className="text-danger">*</span>
            </label>
            <textarea
              className={`form-input${reasonError ? ' form-field-error' : ''}`}
              rows={3}
              placeholder="Why is this employee being terminated? (minimum 5 characters)"
              value={reason}
              onChange={(e) => { setReason(e.target.value); if (reasonTouched && e.target.value.trim().length >= 5) setReasonTouched(true); }}
              onBlur={() => setReasonTouched(true)}
              disabled={terminateLoading}
            />
            {reasonError && <div className="form-error-message">{reasonError}</div>}
          </div>

          {/* Preview state — error / blockers / summary */}
          {previewError && (
            <div style={{
              marginTop: '0.75rem', padding: '0.5rem 0.75rem',
              background: '#fef2f2', border: '1px solid #fecaca',
              color: '#991b1b', borderRadius: '0.375rem', fontSize: '0.8125rem',
            }}>
              Failed to load preview: {previewError.message}
            </div>
          )}

          {preview && blockers.length > 0 && (
            <div style={{
              marginTop: '0.75rem', padding: '0.6rem 0.75rem',
              background: '#fef2f2', border: '1px solid #fecaca',
              color: '#991b1b', borderRadius: '0.375rem', fontSize: '0.8125rem',
            }}>
              <div style={{ fontWeight: 700, marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <IconSystem.warning size={14} />
                Cannot terminate — resolve the following first:
              </div>
              <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
                {blockers.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            </div>
          )}

          {preview && blockers.length === 0 && (
            <div style={{
              marginTop: '0.75rem', padding: '0.6rem 0.75rem',
              background: '#f0fdf4', border: '1px solid #bbf7d0',
              color: '#166534', borderRadius: '0.375rem', fontSize: '0.8125rem',
            }}>
              <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <IconSystem.check size={14} />
                Ready to terminate.
              </div>
              {weeks.some(w => w.plannedAction === 'AUTO_SUBMIT_ZERO') && (
                <div style={{ marginTop: '0.25rem' }}>
                  {weeks.filter(w => w.plannedAction === 'AUTO_SUBMIT_ZERO').length} week(s) will be auto-submitted with zero hours.
                </div>
              )}
            </div>
          )}

          {/* Counts */}
          {preview && (
            <div style={{
              marginTop: '0.75rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap',
            }}>
              <div style={summaryCellStyle}>
                <span style={summaryLabelStyle}>Active Reportees</span>
                <span style={{ ...summaryValueStyle, color: preview.activeReporteeCount > 0 ? '#dc2626' : 'inherit' }}>
                  {preview.activeReporteeCount}
                </span>
              </div>
              <div style={summaryCellStyle}>
                <span style={summaryLabelStyle}>Active Managed Engagements</span>
                <span style={{ ...summaryValueStyle, color: preview.activeManagedEngagementCount > 0 ? '#dc2626' : 'inherit' }}>
                  {preview.activeManagedEngagementCount}
                </span>
              </div>
              <div style={summaryCellStyle}>
                <span style={summaryLabelStyle}>Unsubmitted Weeks</span>
                <span style={summaryValueStyle}>{weeks.length}</span>
              </div>
            </div>
          )}

          {/* Unsubmitted weeks list */}
          {preview && weeks.length > 0 && (
            <div style={{ marginTop: '0.75rem' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.3rem', fontSize: '0.8125rem' }}>
                Unsubmitted Timesheet Weeks
              </div>
              <div style={{ maxHeight: '180px', overflow: 'auto', border: '1px solid var(--border-color)', borderRadius: '0.375rem' }}>
                <table style={{ width: '100%', fontSize: '0.8125rem', borderCollapse: 'collapse' }}>
                  <thead style={{ background: 'var(--bg-secondary)', position: 'sticky', top: 0 }}>
                    <tr>
                      <th style={thStyle}>Week</th>
                      <th style={thStyle}>Status</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Logged</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Required</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Shortfall</th>
                      <th style={thStyle}>Plan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weeks.map((w) => {
                      const plan = plannedActionLabel(w.plannedAction);
                      return (
                        <tr key={w.weekStartDate} style={{ borderTop: '1px solid var(--border-color)' }}>
                          <td style={tdStyle}>{formatDate(w.weekStartDate)} – {formatDate(w.weekEndDate)}</td>
                          <td style={tdStyle}>{w.status}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{formatMinutes(w.minutesLogged)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{formatMinutes(w.minutesRequired)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: w.shortfall > 0 ? '#dc2626' : 'inherit' }}>
                            {formatMinutes(w.shortfall)}
                          </td>
                          <td style={tdStyle}><span className={plan.cls}>{plan.label}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            Terminating is one-way in this version — reactivation will be added in a future update.
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={terminateLoading}>
            Cancel
          </button>
          <button
            className="btn"
            style={{ background: '#dc2626', color: 'white', border: 'none', opacity: canConfirm ? 1 : 0.6 }}
            onClick={handleConfirmClick}
            disabled={!canConfirm}
          >
            <IconSystem.user size={16} style={{ marginRight: '0.4rem', verticalAlign: 'middle' }} />
            Terminate
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Confirm Termination"
        message={`This will set ${employee.code} — ${employee.name} to TERMINATED with last working day ${formatDate(dateLeft)}. This is one-way in v1 (no reactivation). Proceed?`}
        confirmLabel="Terminate"
        cancelLabel="Cancel"
        variant="danger"
        loading={terminateLoading}
        loadingMessage="Terminating…"
        onConfirm={handleDoTerminate}
        onCancel={() => { if (!terminateLoading) setConfirmOpen(false); }}
      />
    </>
  );
};

const summaryCellStyle: React.CSSProperties = {
  flex: 1,
  minWidth: '140px',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.15rem',
  padding: '0.5rem 0.75rem',
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-color)',
  borderRadius: '0.375rem',
};
const summaryLabelStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--text-muted)',
};
const summaryValueStyle: React.CSSProperties = {
  fontSize: '1.1rem',
  fontWeight: 700,
};
const thStyle: React.CSSProperties = {
  padding: '0.4rem 0.6rem',
  fontWeight: 600,
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  textAlign: 'left',
  color: 'var(--text-secondary)',
};
const tdStyle: React.CSSProperties = {
  padding: '0.4rem 0.6rem',
};

export default EmployeeTerminateModal;
